/* eslint-disable */
import {
    Component,
    ElementRef,
    Inject,
    Input,
    OnDestroy,
    Optional,
    Self,
    ViewChild,
} from '@angular/core';
import { Subject } from 'rxjs';
import { DynamicFormFieldOption } from '@app/shared/models/dynamic-form.model';
import { MatSelectChange } from '@angular/material/select';
import {
    ControlValueAccessor,
    FormBuilder,
    FormControl,
    FormGroup,
    NgControl,
} from '@angular/forms';
import { MAT_FORM_FIELD, MatFormField, MatFormFieldControl } from '@angular/material/form-field';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { FocusMonitor } from '@angular/cdk/a11y';

@Component({
    selector: 'app-tree-view-select',
    templateUrl: './tree-view-select.component.html',
    styleUrls: ['./tree-view-select.component.scss'],
    providers: [{ provide: MatFormFieldControl, useExisting: TreeViewSelectComponent }],
    standalone: false
})
export class TreeViewSelectComponent
    implements OnDestroy, ControlValueAccessor, MatFormFieldControl<DynamicFormFieldOption>
{
    static nextId = 0;

    @ViewChild('select') selectElement;
    @Input('aria-describedby') userAriaDescribedBy: string;
    @Input() options: DynamicFormFieldOption[];

    touched = false;
    focused = false;
    controlType = 'example-tel-input';
    id = `example-tel-input-${TreeViewSelectComponent.nextId++}`;
    stateChanges = new Subject<void>();
    formGroup: FormGroup<{
        option: FormControl;
    }>;

    private _placeholder: string;
    private _required = false;
    private _disabled = false;

    onChange = (_: any) => {};
    onTouched = () => {};

    get empty() {
        return !this.formGroup.value.option;
    }

    get shouldLabelFloat() {
        return this.focused || !this.empty;
    }

    @Input()
    get placeholder(): string {
        return this._placeholder;
    }
    set placeholder(value: string) {
        this._placeholder = value;
        this.stateChanges.next();
    }

    @Input()
    get required(): boolean {
        return this._required;
    }
    set required(value: BooleanInput) {
        this._required = coerceBooleanProperty(value);
        this.stateChanges.next();
    }

    @Input()
    get disabled(): boolean {
        return this._disabled;
    }
    set disabled(value: BooleanInput) {
        this._disabled = coerceBooleanProperty(value);
        this._disabled ? this.formGroup.disable() : this.formGroup.enable();
        this.stateChanges.next();
    }

    @Input()
    get value(): DynamicFormFieldOption | null {
        if (this.formGroup.valid) {
            const {
                value: { option },
            } = this.formGroup;
            return option;
        }
        return null;
    }
    set value(option: DynamicFormFieldOption | null) {
        const val = option || undefined;
        this.formGroup.setValue({ option });
        this.stateChanges.next();
    }

    get errorState(): boolean {
        return this.formGroup.invalid && this.touched;
    }

    constructor(
        formBuilder: FormBuilder,
        private _focusMonitor: FocusMonitor,
        private _elementRef: ElementRef<HTMLElement>,
        @Optional() @Inject(MAT_FORM_FIELD) public _formField: MatFormField,
        @Optional() @Self() public ngControl: NgControl
    ) {
        if (this.ngControl != null) {
            this.ngControl.valueAccessor = this;
        }

        this.formGroup = formBuilder.group({
            option: [undefined],
        });
    }

    ngOnDestroy() {
        this.stateChanges.complete();
        this._focusMonitor.stopMonitoring(this._elementRef);
    }

    onFocusIn(event: FocusEvent) {
        if (!this.focused) {
            this.focused = true;
            this.stateChanges.next();
        }
    }

    onFocusOut(event: FocusEvent) {
        if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
            this.touched = true;
            this.focused = false;
            this.onTouched();
            this.stateChanges.next();
        }
    }

    setDescribedByIds(ids: string[]) {
        const controlElement = this._elementRef.nativeElement.querySelector(
            '.example-tel-input-container'
        )!;
        controlElement.setAttribute('aria-describedby', ids.join(' '));
    }

    onContainerClick() {
        if (this.formGroup.controls.option.valid) {
            this._focusMonitor.focusVia(this.selectElement, 'program');
        }
    }

    onSelectionChange(event: MatSelectChange) {
        this.markAsTouched();
        const option = event.value as DynamicFormFieldOption;
        this.formGroup.setValue({ option });
        this.onChange(option);
    }

    writeValue(selectedOption: DynamicFormFieldOption) {
        this.formGroup.setValue({ option: selectedOption });
    }

    registerOnChange(onChange: any) {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any) {
        this.onTouched = onTouched;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    setDisabledState(disabled: boolean) {
        this.disabled = disabled;
    }
}
